import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api } from 'boot/receiver/axios'
import axios from 'axios'

import { $t } from 'boot/common/i18n'
import { useNotificationsStore } from './notifications'
import { useErrorStore } from 'stores/common/error'

import { useCommonAuthStore } from 'stores/common/rulesEditor/commonAuth'

import type { FetchUserRes, User } from 'types/receiver/account/user'
import { useCookieHelpers } from 'src/composables/common/auth/useCookieHelpers'

export const useUserStore = defineStore('user', () => {
  // USER
  const user = ref<null | User>(null)

  const fetchUserInfo = async () => {
    try {
      const res = await api.get<FetchUserRes>('/account/me')

      const data = res?.data
      if (data && data.success) {
        user.value = data.payload

        const notificationStore = useNotificationsStore()
        notificationStore.notifCount = data.payload.total_notifications
        notificationStore.newNotif = data.payload.new_notifications
      }
      return data.success
    } catch (error) {
      const errorStore = useErrorStore()
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        errorStore.backErrors.generalError = $t('error.anError')
      } else {
        errorStore.updateBackErrors(error)
      }

      return false
    }
  }

  const closeUserAccount = async (email: string, sudo_code: string) => {
    try {
      const res = await api.post('/account/close', {
        email,
        sudo_code,
      })

      if (res.status >= 200 && res.status < 300) {
        const cookieHelpers = useCookieHelpers()
        cookieHelpers.removeRefreshToken()
        cookieHelpers.removeAccessToken()
        const { logoutClean } = useCommonAuthStore()
        logoutClean()
        return true
      } else return false
    } catch (error) {
      useErrorStore().updateBackErrors(error)
      return false
    }
  }

  const isLoggedIn = computed(() => {
    return !!user.value
  })
  const isAdmin = computed(() => {
    return !!user.value && user.value.is_admin
  })

  const alertBanner = computed(() => {
    return user.value?.banner || null
  })

  // ORGANISATIONS
  const organisations = computed(() => {
    return user.value ? user.value.organisations_user_belongs_to : []
  })
  const activeOrganisation = computed(() => {
    const orga = user.value?.organisations_user_belongs_to.find(
      (el) => el.active
    )

    return orga
  })
  const subscription = computed(() => {
    return activeOrganisation.value?.subscription
  })

  // ROLES
  const getRoles = computed(() => {
    if (user.value) {
      return Object.values(user.value?.permissions).map((role) => role.name)
    } else return []
  })

  // STORE RESET
  // Used to reset the store (is given by default by pinia on options api stores but not on composition api stores)
  const $reset = () => {
    user.value = null
  }

  return {
    user,
    isLoggedIn,
    isAdmin,
    alertBanner,
    organisations,
    activeOrganisation,
    subscription,
    getRoles,
    fetchUserInfo,
    closeUserAccount,
    $reset,
  }
})
